import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "tozen_theme";

function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* ignore */
  }
  return null;
}

function readOsTheme() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

const THEME_COLORS = { light: "#ffffff", dark: "#161616" }; // --bg-app per theme

function applyDomTheme(theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme === "light" ? "light" : "dark";

  // Keep the PWA's title-bar/status-bar color in sync with the user's explicit
  // choice (the two prefers-color-scheme metas in index.html only cover the
  // pre-toggle default). An unconditional meta with no `media` attribute
  // takes precedence once present.
  let meta = document.querySelector('meta[name="theme-color"]:not([media])');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", THEME_COLORS[theme] ?? THEME_COLORS.dark);
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStoredTheme() ?? readOsTheme());
  // Only an explicit setTheme/toggleTheme call persists a choice — until then
  // the OS listener below keeps following prefers-color-scheme.
  const [hasExplicitChoice, setHasExplicitChoice] = useState(() => readStoredTheme() !== null);

  useLayoutEffect(() => {
    applyDomTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (hasExplicitChoice) return;
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => setThemeState(e.matches ? "light" : "dark");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [hasExplicitChoice]);

  const persist = (next) => {
    setThemeState(next);
    setHasExplicitChoice(true);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const setTheme = (next) => {
    if (next !== "light" && next !== "dark") return;
    persist(next);
  };

  const toggleTheme = () => persist(theme === "dark" ? "light" : "dark");

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isDark: theme === "dark",
    }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
